import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, School } from "lucide-react";

const empty = { name: "", logo_url: "", phone: "", subscription_status: "trial", subscription_days: 30 };

const SubBadge = ({ s }) => {
  if (s.is_active && s.subscription_status === "trial")
    return <Badge className="bg-amber-100 text-amber-800">تجريبي — متبقٍ {s.days_remaining} يوم</Badge>;
  if (s.is_active && s.subscription_status === "active")
    return <Badge className="bg-green-100 text-green-800">فعّال — متبقٍ {s.days_remaining} يوم</Badge>;
  return <Badge className="bg-red-100 text-red-700">غير مفعّل</Badge>;
};

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/schools").then((r) => setSchools(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, logo_url: s.logo_url, phone: s.phone,
      subscription_status: s.subscription_status || "trial", subscription_days: 30 });
    setEditId(s.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("اسم المدرسة مطلوب"); return; }
    try {
      const payload = { ...form };
      if (form.subscription_status !== "active") payload.subscription_days = null;
      if (editId) await api.put(`/schools/${editId}`, payload);
      else await api.post("/schools", payload);
      toast.success(editId ? "تم تعديل المدرسة" : "تمت إضافة المدرسة");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/schools/${id}`); toast.success("تم حذف المدرسة"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">المدارس</h1>
          <p className="text-slate-400">إدارة المدارس المشتركة وحالة اشتراكها</p>
        </div>
        <Button onClick={openAdd} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="add-school-button">
          <Plus className="h-4 w-4" /> إضافة مدرسة
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">المدرسة</TableHead>
                <TableHead className="text-right">الهاتف</TableHead>
                <TableHead className="text-center">المرشدون</TableHead>
                <TableHead className="text-center">الطلاب</TableHead>
                <TableHead className="text-center">الاشتراك</TableHead>
                <TableHead className="text-center">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map((s) => (
                <TableRow key={s.id} data-testid={`school-row-${s.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {s.logo_url ? (
                        <img src={s.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100">
                          <School className="h-5 w-5 text-emerald-700" />
                        </div>
                      )}
                      <span className="font-semibold text-slate-800">{s.name}</span>
                    </div>
                  </TableCell>
                  <TableCell dir="ltr" className="text-right">{s.phone || "—"}</TableCell>
                  <TableCell className="text-center">{s.counselors_count}</TableCell>
                  <TableCell className="text-center">{s.students_count}</TableCell>
                  <TableCell className="text-center"><SubBadge s={s} /></TableCell>
                  <TableCell>
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`}>
                        <Pencil className="h-4 w-4 text-emerald-700" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-school-${s.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="text-right">
                          <AlertDialogHeader>
                            <AlertDialogTitle>حذف المدرسة</AlertDialogTitle>
                            <AlertDialogDescription>
                              سيتم حذف المدرسة "{s.name}" مع جميع المرشدين والطلاب المرتبطين بها. لا يمكن التراجع.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>إلغاء</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(s.id)} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {schools.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400">لا توجد مدارس بعد</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="text-right">
          <DialogHeader><DialogTitle>{editId ? "تعديل مدرسة" : "إضافة مدرسة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs text-slate-500">اسم المدرسة *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">رابط الشعار (اختياري)</Label>
              <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} dir="ltr" data-testid="school-logo" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">هاتف المدرسة</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" data-testid="school-phone" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">حالة الاشتراك</Label>
              <Select value={form.subscription_status} onValueChange={(v) => setForm({ ...form, subscription_status: v })}>
                <SelectTrigger data-testid="school-subscription"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">فترة تجريبية (14 يوم)</SelectItem>
                  <SelectItem value="active">فعّال (عدد أيام محدد)</SelectItem>
                  <SelectItem value="inactive">غير مفعّل</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.subscription_status === "active" && (
              <div>
                <Label className="mb-1 block text-xs text-slate-500">عدد أيام الاشتراك</Label>
                <Input type="number" min="1" value={form.subscription_days}
                  onChange={(e) => setForm({ ...form, subscription_days: parseInt(e.target.value || "0", 10) })}
                  dir="ltr" className="text-right" data-testid="school-days" />
                <p className="mt-1 text-xs text-slate-400">مثال: 120 للفصل الدراسي، 240 للسنة الدراسية.</p>
              </div>
            )}
            {editId && (
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
                تغيير الحالة أو عدد الأيام سيعيد احتساب مدة الاشتراك من اليوم.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="save-school-button">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
